<template class="VcfsTrialBalance">
    <div class="tabs-container">
        <div class="top-tabs tabs-buttons">
            <div v-for="tab in tabConfig" :key="tab.key" class="tabs">
            <button
                :class="`button-${tab.position} ${currentTab === tab.key ? 'selected' : 'unselected'}`"
                :disabled="currentTab === tab.key"
                :title="currentTab === tab.key ? `Currently viewing ${tab.label}` : `Click to view ${tab.label}`"
                @click="currentTab = tab.key"
            >
                {{ tab.label }}
            </button>
            </div>
        </div>
    </div>

    <div class="content-container">
        <div class="top-content">
            <div class="flex-row-centered">
                <div v-if="currentTab == tabs.TRIAL_BALANCE_PERIOD" class="center-buttons">
                    <button @click.prevent="Consolidation" class="incident-nav-button" :disabled="disableConsolidation || DenyFunctionLevelAccess(FunctionAccessLevels.VcfsConsolidation, true)" :title="disableConsolidation ? 'This period has already been consolidated' : (DenyFunctionLevelAccess(FunctionAccessLevels.VcfsConsolidation, true) ? 'You are not authorised to perform this action' : '')"> Consolidate Trial Balance </button>
                </div>
                <PeriodSelector v-model=period showTitle/>
                <div v-if="currentTab == tabs.TRIAL_BALANCE_PERIOD" class="center-buttons">
                    <button class="incident-nav-button" :disabled="disableExport" :title="disableExport? 'No data to export' : ''" @click="ExportData">Export Data</button>
                </div>
            </div>
        </div>

        <!-- Period Trial Balance Table-->
        <div v-if="isLoadingTopLevel" class="content-container">
            <div class="loading">
                <span class="loader"></span>
            </div>
        </div>
        <div class="table-container" v-if="currentTab == tabs.TRIAL_BALANCE_PERIOD && !isLoadingTopLevel">
            <div class="export-csv">
                <ExportCsv :dataToDownload="periodTrialBalanceRows" :fileName="'VCFS_Trial_Balance_For_Period_'+ period" :columnsToRemove="['period']" />
            </div>
            <TablePersistentChecking
                :Rows="periodTrialBalanceRows"
                :Cols="trialBalanceCols"
                :Labels="trialBalanceLabels"
                :RowClickedCallBack=expansionGetterPeriod
                >
                <template #account="{value}">
                    <div> {{ formatTitileFromCanonicalWithExceptions(value.account) }} </div>
                </template>
                <template #totalDebit="{ value }">
                    <div> {{ MonetaryValueWithCommas(value.totalDebit) }} </div>
                </template>
                <template #totalCredit="{ value }">
                    <div> {{ MonetaryValueWithCommas(value.totalCredit) }} </div>
                </template>

                <template #post-row="{ value }">
                    <tr
                        v-if="expanded && expandedRowAccount === value.account"
                        :class="['inner-table-row']"
                    >
                        <td class="small"></td>
                        <td :colspan="trialBalanceCols.length + 20" class="inner-table-cont">
                            <div v-if="isLoading" class="content-container">
                                <div class="loading">
                                    <span class="loader"></span>
                                </div>
                            </div>
                            <div class="inner-table-cont">
                                <div class="export-csv">
                                    <ExportCsv
                                    :dataToDownload="periodTrialBalanceRowsExpandable"
                                    :fileName="'VCFS_Trial_Balance_For_Period_'+ period + '_items'"
                                    />
                                </div>
                                <TablePersistentChecking
                                    class="inner-table"
                                    :Rows="periodTrialBalanceRowsExpandable"
                                    :Cols="trialBalanceColsExpandable"
                                    :Labels="trialBalanceLabelsExpandable"
                                    :RowClickedCallBack='(row) => ViewJNLReference(row, expandedRowAccount, period.toString())'
                                >
                                <template #journalType="{value}">
                                    <div> {{ formatTitileFromCanonicalWithExceptions(value.journalType) }} </div>
                                </template>
                                <template #debitAmount="{ value }">
                                    <div>
                                        {{ MonetaryValueWithCommas(value.debitAmount) }}
                                    </div>
                                </template>
                                <template #creditAmount="{ value }">
                                    <div>
                                        {{ MonetaryValueWithCommas(value.creditAmount) }}
                                    </div>
                                </template>
                                </TablePersistentChecking>
                            </div>

                        </td>
                    </tr>
                </template>
            </TablePersistentChecking>
        </div>
        
        <!-- Up To Period Trial Balance Top Level Table -->
        <div class="table-container" v-if="currentTab == tabs.TRIAL_BALANCE_ACCUMULATED && !isLoadingTopLevel">
            <div class="export-csv">
                <ExportCsv
                :dataToDownload="upToPeriodTrialBalanceTopLevelRows"
                :fileName="'VCFS_Trial_Balance_Up_To_Period('+ period +')'"
                />
            </div>
            <TablePersistentChecking
            :Rows="upToPeriodTrialBalanceTopLevelRows"
            :Cols="topLevelUpToPeriodCols"
            :Labels="topLevelUpToPeriodTrialBalanceLabels"
            :RowClickedCallBack=expansionGetterUpToPeriodTopLevel
            >
            <template #account="{value}">
                <div> {{ formatTitileFromCanonicalWithExceptions(value.account?? "") }} </div>
            </template>
            <template #totalDebit="{ value }">
                <div> {{ MonetaryValueWithCommas(value.totalDebit) }} </div>
            </template>
            <template #totalCredit="{ value }">
                <div> {{ MonetaryValueWithCommas(value.totalCredit) }} </div>
            </template>
            <!--second layer up to period table -->
            <template #post-row="{ value }">
                    <tr
                        v-if="expandedUpToPeriodTopLevel && expandedRowAccountUpToPeriodTopLevel === value.account"
                        :class="['inner-table-row']"
                    >
                        <td class="small"></td>
                        <td :colspan="topLevelUpToPeriodCols.length + 20" class="inner-table-cont">
                            <div v-if="isLoading" class="content-container">
                                <div class="loading">
                                    <span class="loader"></span>
                                </div>
                            </div>
                            <div v-else class="table-container">
                                <div class="inner-table-cont"> 
                                    <div class="export-csv">
                                        <ExportCsv
                                        :dataToDownload="upToPeriodTrialBalanceRowsExpandableTopLevel"
                                        :fileName="expandedRowAccountUpToPeriodTopLevel + '_' + 'VCFS_Trial_Balance_Up_To_Period'"
                                        />
                                    </div>
                                    <TablePersistentChecking
                                        class="inner-table"
                                        :Rows="upToPeriodTrialBalanceRowsExpandableTopLevel"
                                        :Cols="upToPeriodTrialBalanceCols"
                                        :Labels="upToPeriodTrialBalanceLabels"
                                        :RowClickedCallBack=expansionGetterUpToPeriod
                                    >
                                    <template #account="{value}">
                                        <div> {{ formatTitileFromCanonicalWithExceptions(value.account) }} </div>
                                    </template>
                                    <template #totalDebit="{ value }">
                                        <div> {{ MonetaryValueWithCommas(value.totalDebit) }} </div>
                                    </template>
                                    <template #totalCredit="{ value }">
                                        <div> {{ MonetaryValueWithCommas(value.totalCredit) }} </div>
                                    </template>
                                    <template #post-row="{ value }">
                                        <div v-if="expandedUpToPeriod && expandedRowAccountUpToPeriod === value.account && expandedRowPeriodUpToPeriod === value.period"> 

                                        </div>
                                        <tr
                                            v-if="expandedUpToPeriod && expandedRowAccountUpToPeriod === value.account && expandedRowPeriodUpToPeriod === value.period"
                                            :class="['inner-table-row']"
                                        >
                                            <td class="small"></td>
                                            <td :colspan="upToPeriodTrialBalanceCols.length + 20">
                                                <div class="inner-table-cont">
                                                    <div v-if="isLoading" class="content-container">
                                                        <div class="loading">
                                                            <span class="loader"></span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div class="export-csv">
                                                            <ExportCsv
                                                            :dataToDownload="upToPeriodTrialBalanceRowsExpandable"
                                                            :fileName="expandedRowAccountUpToPeriod + '_' + expandedRowPeriodUpToPeriod + '_' + 'VCFS_Trial_Balance_Up_To_Period'"
                                                            />
                                                        </div>
                                                        <!--third table layer-->
                                                        <TablePersistentChecking
                                                            class="inner-table"
                                                            :Rows="upToPeriodTrialBalanceRowsExpandable"
                                                            :Cols="trialBalanceColsExpandable"
                                                            :Labels="trialBalanceLabelsExpandable"
                                                            :RowClickedCallBack='(row) => ViewJNLReference(row, expandedRowAccountUpToPeriod, expandedRowPeriodUpToPeriod)'
                                                        >
                                                        <template #journalType="{value}">
                                                            <div> {{ formatTitileFromCanonicalWithExceptions(value.journalType) }} </div>
                                                        </template>
                                                        <template #debitAmount="{ value }">
                                                            <div> {{ MonetaryValueWithCommas(value.debitAmount) }} </div>
                                                        </template>
                                                        <template #creditAmount="{ value }">
                                                            <div> {{ MonetaryValueWithCommas(value.creditAmount) }} </div>
                                                        </template>
                                                        </TablePersistentChecking>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                    </TablePersistentChecking>
                                </div>
                            </div>
                        </td>
                    </tr>
                </template>
            </TablePersistentChecking>
        </div>

        <!-- Profit and Loss Table-->
        <div class="table-container" v-if="currentTab == tabs.PROFIT_AND_LOSS && !isLoadingTopLevel">
            <div class="export-csv">
                <ExportCsv :dataToDownload="profitAndLossRows" :fileName="'VCFS_Trial_Balance_Profit_And_Loss'+ period" :columnsToRemove="['period']" />
            </div>
            <TablePersistentChecking
                :Rows="profitAndLossRows"
                :Cols="trialBalanceCols"
                :Labels="trialBalanceLabels"
                :RowClickedCallBack=expansionGetterPeriod
                :SummaryCols="summaryColsProfitAndLoss"
                :SummaryValues="summaryValuesProfitAndLoss"
                SummaryLabel="Subtotal"
                >
                <template #account="{value}">
                    <div> {{ formatTitileFromCanonicalWithExceptions(value.account) }} </div>
                </template>
                <template #totalDebit="{ value }">
                    <div> {{ MonetaryValueWithCommas(value.totalDebit) }} </div>
                </template>
                <template #totalCredit="{ value }">
                    <div> {{ MonetaryValueWithCommas(value.totalCredit) }} </div>
                </template>

                <template #post-row="{ value }">
                    <tr
                        v-if="expanded && expandedRowAccount === value.account"
                        :class="['inner-table-row']"
                    >
                        <td class="small"></td>
                        <td :colspan="trialBalanceCols.length + 20" class="inner-table-cont">
                            <div v-if="isLoading" class="content-container">
                                <div class="loading">
                                    <span class="loader"></span>
                                </div>
                            </div>
                            <div class="inner-table-cont">
                                <div class="export-csv">
                                    <ExportCsv
                                    :dataToDownload="periodTrialBalanceRowsExpandable"
                                    :fileName="'VCFS_Trial_Balance_Profit_And_Loss'+ period + '_items'"
                                    />
                                </div>
                                <TablePersistentChecking
                                    class="inner-table"
                                    :Rows="periodTrialBalanceRowsExpandable"
                                    :Cols="trialBalanceColsExpandable"
                                    :Labels="trialBalanceLabelsExpandable"
                                    :RowClickedCallBack='(row) => ViewJNLReference(row, expandedRowAccount, period.toString())'
                                >
                                <template #journalType="{value}">
                                    <div> {{ formatTitileFromCanonicalWithExceptions(value.journalType) }} </div>
                                </template>
                                <template #debitAmount="{ value }">
                                    <div>
                                        {{ MonetaryValueWithCommas(value.debitAmount) }}
                                    </div>
                                </template>
                                <template #creditAmount="{ value }">
                                    <div>
                                        {{ MonetaryValueWithCommas(value.creditAmount) }}
                                    </div>
                                </template>
                                </TablePersistentChecking>
                            </div>

                        </td>
                    </tr>
                </template>
            </TablePersistentChecking>

            <h4 v-if="totalProfit>=0">Total Profit: <span style="color:green;">{{  MonetaryValueWithCommas(totalProfit) }}</span></h4>
            <h4 v-else>Total Loss: <span style="color:red;">{{  MonetaryValueWithCommas(totalProfit) }}</span></h4>
        </div>

        <!-- Balance Sheet Top Level Table -->
        <div class="table-container" v-if="currentTab == tabs.BALANCE_SHEET && !isLoadingTopLevel">
        <div class="export-csv">
            <ExportCsv
            :dataToDownload="balanceSheetTopLevelRows"
            :fileName="'VCFS_Balance_Sheet_Up_To_Period_' + period"
            />
        </div>
        <TablePersistentChecking
            :Rows="balanceSheetTopLevelRows"
            :Cols="balanceSheetTopLevelCols"
            :Labels="balanceSheetLabelsTopLevel"
            :RowClickedCallBack="expansionGetterBalanceSheetTopLevel"
            :SummaryCols="summaryColsBalanceSheet"
            :SummaryValues="summaryValuesBalanceSheet"
            SummaryLabel="Subtotal"
        >
            <template #account="{value}">
            <div> {{ formatTitileFromCanonicalWithExceptions(value.account?? "") }} </div>
            </template>
            <template #totalDebit="{ value }">
            <div>{{ MonetaryValueWithCommas(value.totalDebit) }}</div>
            </template>
            <template #totalCredit="{ value }">
            <div>{{ MonetaryValueWithCommas(value.totalCredit) }}</div>
            </template>

            <!-- Second layer -->
            <template #post-row="{ value }">
            <tr
                v-if="expandedBalanceSheetTopLevel && expandedRowAccountBalanceSheetTopLevel === value.account"
                class="inner-table-row"
            >
                <td class="small"></td>
                <td :colspan="balanceSheetTopLevelCols.length + 20" class="inner-table-cont">
                <div v-if="isLoading" class="content-container">
                    <div class="loading"><span class="loader"></span></div>
                </div>
                <div v-else class="table-container">
                    <div class="inner-table-cont">
                    <div class="export-csv">
                        <ExportCsv
                        :dataToDownload="balanceSheetRowsExpandableTopLevel"
                        :fileName="expandedRowAccountBalanceSheetTopLevel + '_VCFS_Balance_Sheet_Detail'"
                        />
                    </div>
                    <TablePersistentChecking
                        class="inner-table"
                        :Rows="balanceSheetRowsExpandableTopLevel"
                        :Cols="balanceSheetCols"
                        :Labels="balanceSheetLabels"
                        :RowClickedCallBack="expansionGetterBalanceSheet"
                    >
                        <template #account="{value}">
                            <div> {{ formatTitileFromCanonicalWithExceptions(value.account) }} </div>
                        </template>
                        <template #totalDebit="{ value }">
                        <div>{{ MonetaryValueWithCommas(value.totalDebit) }}</div>
                        </template>
                        <template #totalCredit="{ value }">
                        <div>{{ MonetaryValueWithCommas(value.totalCredit) }}</div>
                        </template>

                        <!-- Third layer -->
                        <template #post-row="{ value }">
                        <tr
                            v-if="expandedBalanceSheet && expandedRowAccountBalanceSheet === value.account && expandedRowPeriodBalanceSheet === value.period"
                            class="inner-table-row"
                        >
                            <td class="small"></td>
                            <td :colspan="balanceSheetCols.length + 20">
                            <div class="inner-table-cont">
                                <div v-if="isLoading" class="content-container">
                                <div class="loading"><span class="loader"></span></div>
                                </div>
                                <div>
                                <div class="export-csv">
                                    <ExportCsv
                                    :dataToDownload="balanceSheetRowsExpandable"
                                    :fileName="expandedRowAccountBalanceSheet + '_' + expandedRowPeriodBalanceSheet + '_VCFS_Balance_Sheet_Items'"
                                    />
                                </div>
                                <TablePersistentChecking
                                    class="inner-table"
                                    :Rows="balanceSheetRowsExpandable"
                                    :Cols="trialBalanceColsExpandable"
                                    :Labels="trialBalanceLabelsExpandable"
                                    :RowClickedCallBack="(row) => ViewJNLReference(row, expandedRowAccountBalanceSheet, expandedRowPeriodBalanceSheet)"
                                >
                                    <template #journalType="{value}">
                                        <div> {{ formatTitileFromCanonicalWithExceptions(value.journalType) }} </div>
                                    </template>
                                    <template #debitAmount="{ value }">
                                    <div>{{ MonetaryValueWithCommas(value.debitAmount) }}</div>
                                    </template>
                                    <template #creditAmount="{ value }">
                                    <div>{{ MonetaryValueWithCommas(value.creditAmount) }}</div>
                                    </template>
                                </TablePersistentChecking>
                                </div>
                            </div>
                            </td>
                        </tr>
                        </template>
                    </TablePersistentChecking>
                    </div>
                </div>
                </td>
            </tr>
            </template>
        </TablePersistentChecking>
        <h4 v-if="totalBalance>=0">Total Balance: <span style="color:green;">{{ MonetaryValueWithCommas(totalBalance) }}</span></h4>
        <h4 v-else>Total Balance: <span style="color:red;">{{  MonetaryValueWithCommas(totalBalance) }}</span></h4>
        </div>

        <VcfsTrialBalancePopUp
        v-model="popUpOpen"
        :tableRows="vcfsTrialBalancePopUpRows"
        :journalAccount="journalAccountPopUp"
        :journalReference="journalReferencePopUp"
        >
        </VcfsTrialBalancePopUp>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from "vue";
import TablePersistentChecking from "@/components/TablePersistentChecking.vue";
import DatePeriod from "@/types/DatePeriod";
import PeriodSelector from '@/components/PeriodSelector.vue';
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";
import { DisplayableKeys } from "@/services/SorterService";
import {  MonetaryValueWithCommas, formatTitileFromCanonicalWithExceptions } from "@/services/helpers/Helper";
import { downloadManager, vcfsConsolidation, vcfsPeriodConsituentItemsTrialBalance, vcfsTrialBalance, vcfsTrialBalanceJournalReferenceItems } from "@/services/network";
import { IVcfsTrialBalance } from "src/Interfaces/VcfsTrialBalance/IVcfsTrialBalance";
import { IVcfsTrialBalanceConstituentItem } from "src/Interfaces/VcfsTrialBalance/IVcfsTrialBalanceConstituentItem";
import VcfsTrialBalancePopUp from "@/components/VcfsTrialBalancePopUp.vue";
import ExportCsv from "@/components/ExportCsv.vue";
import useSpinnerPopup from "@/services/composables/SpinnerPopup";
import { authService } from "@/auth/authSingleton";
import { FunctionAccessLevels, DenyFunctionLevelAccess } from "@/config/AuthConfig";
import { VcfsTrialBalanceStatementCategories } from "@/models/enums/VcfsTrialBalanceStatementCategories";
import { FileDownloadType } from "@/models/enums/FileDownloadTypes";

enum tabs {
    TRIAL_BALANCE_PERIOD = "TRIAL_BALANCE_PERIOD",
    TRIAL_BALANCE_ACCUMULATED = "TRIAL_BALANCE_ACCUMULATED",
    PROFIT_AND_LOSS = "PROFIT_AND_LOSS",
    BALANCE_SHEET = "BALANCE_SHEET"
}
const tabConfig = [
  { key: tabs.TRIAL_BALANCE_PERIOD, label: "Trial Balance - Period", position: "left" },
  { key: tabs.TRIAL_BALANCE_ACCUMULATED, label: "Trial Balance - Accumulated", position: "middle" },
  { key: tabs.PROFIT_AND_LOSS, label: "Profit & Loss", position: "middle" },
  { key: tabs.BALANCE_SHEET, label: "Balance Sheet", position: "right" }
];
const currentTab = ref<tabs>(tabs.TRIAL_BALANCE_PERIOD);
const period = ref(new DatePeriod(new Date().addMonths(-1)));
const getVcfsPeriodTrialBalanceError = ref(false)
const isLoading = ref(false);
const isLoadingTopLevel = ref(false);
const expanded = ref<boolean>(false);
const expandedUpToPeriod = ref<boolean>(false);
const expandedUpToPeriodTopLevel = ref<boolean>(false);
const expandedRowAccount = ref();
const expandedRowAccountUpToPeriod = ref();
const expandedRowPeriodUpToPeriod = ref();
const expandedRowAccountUpToPeriodTopLevel = ref();
const upToPeriodTrialBalanceRows = ref<IVcfsTrialBalance[]>([]);
const upToPeriodTrialBalanceTopLevelRows = ref<Partial<IVcfsTrialBalance>[]>([]);
const balanceSheetTopLevelRows = ref<Partial<IVcfsTrialBalance>[]>([]);
const periodTrialBalanceRows = ref<IVcfsTrialBalance[]>([]);
const profitAndLossRows = ref<IVcfsTrialBalance[]>([]);
const balanceSheetRows = ref<IVcfsTrialBalance[]>([]);
const periodTrialBalanceRowsExpandable = ref<IVcfsTrialBalanceConstituentItem[]>([]);
const upToPeriodTrialBalanceRowsExpandable = ref<IVcfsTrialBalanceConstituentItem[]>([]);
const upToPeriodTrialBalanceRowsExpandableTopLevel = ref<IVcfsTrialBalance[]>([]);
const balanceSheetRowsExpandableTopLevel = ref<IVcfsTrialBalance[]>([]);
const balanceSheetRowsExpandable = ref<IVcfsTrialBalanceConstituentItem[]>([]);
const expandedBalanceSheetTopLevel = ref(false);
const expandedRowAccountBalanceSheetTopLevel = ref();
const expandedBalanceSheet = ref(false);
const expandedRowAccountBalanceSheet = ref();
const expandedRowPeriodBalanceSheet = ref();
const emailAddress = authService.getEmailAddress();
//popUp 
const vcfsTrialBalancePopUpRows = ref<Record<string, any>[]>([])
const journalAccountPopUp = ref("")
const journalReferencePopUp = ref("")
const popUpOpen = ref(false);
//Table cols
const trialBalanceCols: Array<keyof IVcfsTrialBalance> = [ "account", "totalDebit", "totalCredit", "tbJournalReference" ];
const topLevelUpToPeriodCols: Array<keyof IVcfsTrialBalance> = [ "account", "totalDebit", "totalCredit", ];
const upToPeriodTrialBalanceCols: Array<keyof IVcfsTrialBalance> = [ "account", "totalDebit", "totalCredit", "tbJournalReference", "period" ];
const trialBalanceColsExpandable: DisplayableKeys<IVcfsTrialBalanceConstituentItem>[] = ['journalReference', "debitAmount", "creditAmount", "journalType", "ownerId", "journalingDate"];
const balanceSheetTopLevelCols: Array<keyof IVcfsTrialBalance> = ["account", "totalDebit", "totalCredit"];
const balanceSheetCols: Array<keyof IVcfsTrialBalance> = ["account", "totalDebit", "totalCredit", "tbJournalReference", "period"];
//Table labs
const topLevelUpToPeriodTrialBalanceLabels = [ "Account", "Total Debit", "Total Credit"]
const trialBalanceLabels = [ "Account", "Total Debit", "Total Credit", "TB Journal Reference"]
const trialBalanceLabelsExpandable = [ 'Journal Reference', "Debit Amount", "Credit Amount", "Journaling Type", "Owner ID", "Journaling Date" ];
const upToPeriodTrialBalanceLabels = [ "Account", "Total Debit", "Total Credit", "TB Journal Reference", "Period" ];
const balanceSheetLabelsTopLevel = ["Account", "Total Debit", "Total Credit"];
const balanceSheetLabels = ["Account", "Total Debit", "Total Credit", "TB Journal Reference", "Period"];

const summaryColsProfitAndLoss: DisplayableKeys<IVcfsTrialBalance>[] = ["totalDebit", "totalCredit"]
const summaryColsBalanceSheet: DisplayableKeys<IVcfsTrialBalance>[] = ["totalDebit", "totalCredit"]
watch(profitAndLossRows, (newVal) => {
  console.log("profitAndLossRows updated:", newVal);
  console.log("totalDebit:", totalDebitProfitAndLoss.value);
console.log("totalCredit:", totalCreditProfitAndLoss.value);
console.log("totalProfit:", totalProfit.value)
});
const totalDebitProfitAndLoss = computed(() =>
  Math.round(profitAndLossRows.value.reduce((sum, item) => sum + (Number(item.totalDebit) || 0), 0) * 100) / 100
);
const totalCreditProfitAndLoss = computed(() =>
  Math.round(profitAndLossRows.value.reduce((sum, item) => sum + (Number(item.totalCredit) || 0), 0) * 100) / 100
);
const totalProfit = computed(()=>
    Math.round((totalCreditProfitAndLoss.value - totalDebitProfitAndLoss.value) * 100) / 100
)
const totalDebitBalanceSheet = computed(() =>
  Math.round(balanceSheetTopLevelRows.value.reduce((sum, item) => sum + (Number(item.totalDebit) || 0), 0) * 100) / 100
);
const totalCreditBalanceSheet = computed(() =>
  Math.round(balanceSheetTopLevelRows.value.reduce((sum, item) => sum + (Number(item.totalCredit) || 0), 0) * 100) / 100
);
const totalBalance = computed(()=>
    Math.round((totalDebitBalanceSheet.value - totalCreditBalanceSheet.value ) * 100) / 100
)
const summaryValuesProfitAndLoss = computed(() => [totalDebitProfitAndLoss.value, totalCreditProfitAndLoss.value]);
const summaryValuesBalanceSheet = computed(() => [totalDebitBalanceSheet.value, totalCreditBalanceSheet.value]);
onMounted(async () => {
    document.title = 'Vcfs Trial Balance - UV';
    await getVcfsPeriodTrialBalance();
    await getVcfsUpToPeriodTrialBalance();
});
const disableExport = computed(() => {
    return getVcfsPeriodTrialBalanceError.value == true ||  periodTrialBalanceRows.value.length == 0
});
const disableConsolidation = computed(() => {
    return getVcfsPeriodTrialBalanceError.value == true ||  periodTrialBalanceRows.value.length > 0
});
watch(upToPeriodTrialBalanceRows, (newVal) => {
    if (!newVal || newVal.length === 0) {
        upToPeriodTrialBalanceTopLevelRows.value = [];
        return;
    }
    const grouped = Object.groupBy(newVal, item => item.account)
    const result = Object.entries(grouped).map(([account, items = []]) => {
        const totalDebit = items.reduce((sum, i) => sum + (i.totalDebit ?? 0), 0);
        const totalCredit = items.reduce((sum, i) => sum + (i.totalCredit ?? 0), 0)
        const creditDebitDifference = totalDebit - totalCredit;
        return {
            account,
            totalDebit: creditDebitDifference > 0 ? creditDebitDifference : 0,
            totalCredit: creditDebitDifference < 0 ? -creditDebitDifference : 0,
        };
    });
    upToPeriodTrialBalanceTopLevelRows.value = result;
});
watch(balanceSheetRows, (newVal) => {
  if (!newVal || newVal.length === 0) {
    balanceSheetTopLevelRows.value = [];
    return;
  }
  const grouped = Object.groupBy(newVal, item => item.account);
  const result = Object.entries(grouped).map(([account, items = []]) => {
    const totalDebit = items.reduce((sum, i) => sum + (i.totalDebit ?? 0), 0);
    const totalCredit = items.reduce((sum, i) => sum + (i.totalCredit ?? 0), 0);
    const diff = totalDebit - totalCredit;
    return {
      account,
      totalDebit: diff > 0 ? diff : 0,
      totalCredit: diff < 0 ? -diff : 0
    };
  });
  balanceSheetTopLevelRows.value = result;
});
watch([period, currentTab], async ([newPeriod, newCurrentTab]) => {
    if (!newPeriod) return;
    if (newCurrentTab == tabs.TRIAL_BALANCE_PERIOD) {
        await getVcfsPeriodTrialBalance();
    }
    if(newCurrentTab == tabs.TRIAL_BALANCE_ACCUMULATED){
        await getVcfsUpToPeriodTrialBalance();
    }
    if(newCurrentTab == tabs.PROFIT_AND_LOSS){
        await getVcfsProfitAndLoss()
    }if(newCurrentTab == tabs.BALANCE_SHEET){
        await getVcfsBalanceSheet()
    }
});
async function getVcfsPeriodTrialBalance() {
    if(isLoadingTopLevel.value) return
    resetPeriodTrialBalanceExpandableRow()
    try {
        getVcfsPeriodTrialBalanceError.value = false
        isLoadingTopLevel.value = true
        const data = (await vcfsTrialBalance.get({period: period.value.toString()})).data        
        periodTrialBalanceRows.value = data;
    } catch (error) {
        useNotifierWithErrFormatter().error({errorType:"Getting VCFS Period Trial Balance", error})
        getVcfsPeriodTrialBalanceError.value = true
    } finally {
        isLoadingTopLevel.value = false;
    }
}
async function getVcfsProfitAndLoss() {
    if(isLoadingTopLevel.value) return
    resetPeriodTrialBalanceExpandableRow()
    try {
        isLoadingTopLevel.value = true
        const data = (await vcfsTrialBalance.get({period: period.value.toString(), statementCategory: VcfsTrialBalanceStatementCategories[VcfsTrialBalanceStatementCategories.PL] as keyof typeof VcfsTrialBalanceStatementCategories})).data        
        profitAndLossRows.value = data;
    } catch (error) {
        useNotifierWithErrFormatter().error({errorType:"Getting VCFS Profit and Loss Balance", error})
    } finally {
        isLoadingTopLevel.value = false;
    }
}
async function getVcfsBalanceSheet() {
    if(isLoadingTopLevel.value) return
    resetBalanceSheetExpandableRows()
    try {
        isLoadingTopLevel.value = true
        const data = (await vcfsTrialBalance.get({period: period.value.toString(), statementCategory: VcfsTrialBalanceStatementCategories[VcfsTrialBalanceStatementCategories.BS] as keyof typeof VcfsTrialBalanceStatementCategories})).data        
        balanceSheetRows.value = data;
    } catch (error) {
        useNotifierWithErrFormatter().error({errorType:"Getting VCFS Balance Sheet", error})
    } finally {
        isLoadingTopLevel.value = false;
    }
}
function resetBalanceSheetExpandableRows() {
  expandedBalanceSheetTopLevel.value = false;
  expandedRowAccountBalanceSheetTopLevel.value = null;
  balanceSheetRowsExpandableTopLevel.value = [];

  expandedBalanceSheet.value = false;
  expandedRowAccountBalanceSheet.value = null;
  expandedRowPeriodBalanceSheet.value = null;
  balanceSheetRowsExpandable.value = [];
}

async function expansionGetterBalanceSheetTopLevel(row: Partial<IVcfsTrialBalance>) {
  if (expandedRowAccountBalanceSheetTopLevel.value === row.account) {
    resetBalanceSheetExpandableRows();
  } else {
    isLoading.value = true;
    expandedBalanceSheetTopLevel.value = true;
    expandedRowAccountBalanceSheetTopLevel.value = row.account;
    balanceSheetRowsExpandableTopLevel.value = balanceSheetRows.value.filter(item => item.account === row.account);
    isLoading.value = false;
  }
}

async function expansionGetterBalanceSheet(row: IVcfsTrialBalance) {
  if (expandedRowAccountBalanceSheet.value === row.account && expandedRowPeriodBalanceSheet.value === row.period) {
    expandedBalanceSheet.value = false;
    expandedRowAccountBalanceSheet.value = null;
    expandedRowPeriodBalanceSheet.value = null;
    balanceSheetRowsExpandable.value = [];
  } else {
    expandedBalanceSheet.value = true;
    expandedRowAccountBalanceSheet.value = row.account;
    expandedRowPeriodBalanceSheet.value = row.period;
    isLoading.value = true;
    const data = (await vcfsPeriodConsituentItemsTrialBalance.get(
      { account: row.account },
      { period: row.period.toString() }
    )).data;
    balanceSheetRowsExpandable.value = data;
    isLoading.value = false;
  }
}
async function getVcfsUpToPeriodTrialBalance() {
    if(isLoadingTopLevel.value) return
    resetUpToPeriodTrialBalanceExpandableRow()
    try {
        isLoadingTopLevel.value = true
        const data = (await vcfsTrialBalance.get({period: period.value.toString(), upToPeriod: true })).data        
        upToPeriodTrialBalanceRows.value = data;
    } catch (error) {
        useNotifierWithErrFormatter().error({errorType:"Getting VCFS Up to Period Trial Balance", error})
    } finally {
        isLoadingTopLevel.value = false;
    }
}
function resetPeriodTrialBalanceExpandableRow(){
    expanded.value = false;
    expandedRowAccount.value = null;
    periodTrialBalanceRowsExpandable.value = [];
}
function resetUpToPeriodTrialBalanceExpandableRow(){
    expandedUpToPeriod.value = false;
    expandedRowAccountUpToPeriod.value = null;
    expandedRowPeriodUpToPeriod.value = null;
    upToPeriodTrialBalanceRowsExpandable.value = [];
}
function resetUpToPeriodTrialBalanceExpandableRowTopLevel(){
    expandedUpToPeriodTopLevel.value = false;
    expandedRowAccountUpToPeriodTopLevel.value = null;
    upToPeriodTrialBalanceRowsExpandableTopLevel.value = [];
}
async function expansionGetterPeriod(row: IVcfsTrialBalance){
    if (expandedRowAccount.value === row.account) {
        resetPeriodTrialBalanceExpandableRow()
    } else {
        expanded.value = true;
        expandedRowAccount.value = row.account;           
        isLoading.value = true;    
        const data = (await vcfsPeriodConsituentItemsTrialBalance.get({account:row.account}, {period: period.value.toString()})).data
        periodTrialBalanceRowsExpandable.value = data;
        isLoading.value = false
    }
}
async function expansionGetterUpToPeriod(row: IVcfsTrialBalance){
    if (expandedRowAccountUpToPeriod.value === row.account && expandedRowPeriodUpToPeriod.value == row.period) {
        resetUpToPeriodTrialBalanceExpandableRow()
    } else {
        expandedUpToPeriod.value = true;
        expandedRowAccountUpToPeriod.value = row.account;  
        expandedRowPeriodUpToPeriod.value = row.period;    
        isLoading.value = true;
        const data = (await vcfsPeriodConsituentItemsTrialBalance.get({account:row.account}, {period: row.period.toString()})).data 
        upToPeriodTrialBalanceRowsExpandable.value = data;
        isLoading.value = false
    }
}
async function expansionGetterUpToPeriodTopLevel(row: Partial<IVcfsTrialBalance>) {
    if (expandedRowAccountUpToPeriodTopLevel.value === row.account) {
        resetUpToPeriodTrialBalanceExpandableRowTopLevel()
    } else {
        isLoading.value = true;
        expandedUpToPeriodTopLevel.value = true;
        expandedRowAccountUpToPeriodTopLevel.value = row.account;
        upToPeriodTrialBalanceRowsExpandableTopLevel.value = upToPeriodTrialBalanceRows.value.filter(
            item => item.account === row.account
        );
        isLoading.value = false;
    }
}
async function ViewJNLReference(row: IVcfsTrialBalanceConstituentItem, account: string, period: string){
    const closeSpinner = useSpinnerPopup().show();
    try {
        const journalReference = row.journalReference

        const response = await vcfsTrialBalanceJournalReferenceItems.get({account: account, journalReference: journalReference} , {period: period.toString()}) 
        const data = response.data

        journalAccountPopUp.value = account
        journalReferencePopUp.value = journalReference
        
        if(response.status == 204){
            const message204 = account + " does not support journal reference look up"
            useNotifierWithErrFormatter().notify({msg: message204});
            return
        }
        if (Array.isArray(data) && typeof data[0] === 'object') {
            vcfsTrialBalancePopUpRows.value = data;
            popUpOpen.value = true
            return
        }
        if (!Array.isArray(data) || data.length === 0) {
            const emptyDataMessage = `No data is available for journal reference "${journalReference}".`;
            useNotifierWithErrFormatter().notify({ msg: emptyDataMessage });
            return;
        }
    } catch (error) {
        useNotifierWithErrFormatter().error({errorType:"Getting VCFS Period Trial Balance", error})
    } finally {
        closeSpinner()
    }
}
async function Consolidation(){
    if(isLoading.value) return
    const closeSpinner = useSpinnerPopup().show();
    try{
        isLoading.value = true
        const response = await vcfsConsolidation.post({period: period.value.toString()})
        if(response.status == 204){
            useNotifierWithErrFormatter().notify({msg: "No eligible data to consolidate"})
        }
        else{
            useNotifierWithErrFormatter().success({msg: "VCFS Trial Consolidated"})
            isLoading.value = false
            await getVcfsPeriodTrialBalance()
        }
    }catch(err){
        useNotifierWithErrFormatter().error({errorType: "VCFS Trial Consolidation", error: err})
    }finally{
        isLoading.value = false
        closeSpinner()
    }
}
async function ExportData() {
    const closeSpinner = useSpinnerPopup().show();
    try {
        await downloadManager.post({
            userName: emailAddress,
            type: FileDownloadType.VCFS_ACCOUNTING_REPORT,
            requestData:
            {
                PeriodMonth: new DatePeriod(period.value)
            }
        })
        useNotifierWithErrFormatter().success({
            msg: "VCFS accounting report was queued for download"
        });
    } catch(error) {
        useNotifierWithErrFormatter().error({
            errorType: "VCFS accounting report download queue error",
            error,
        });
    } finally {
        closeSpinner();
    }
}
</script>
<style scoped lang="scss">
@import "@/assets/styles/layout.scss";
@import "@/assets/styles/buttons.scss";
.export-csv {
    display: flex;
    align-items: flex-end;
}
::v-deep(.inner-table-cont) {
    max-height: 450px;
    overflow-y: auto;
    position: relative;
    width: inherit;
    overflow-x: auto;
    height: 100%;
}
.table-highlight-button {
    white-space: nowrap;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}
.table-highlight-button:hover{
    cursor: pointer;  
    color: blue; 
    text-decoration: underline;
}
:deep(.incident-nav-button){
    width: 20rem;
}
</style>
